/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ID } from '@gh/common-shared-data';
import { coalesce } from '@gh/core-util';
import { fromPairs, isNil, keyBy } from 'lodash';
import { createTableMapper } from './table-mapper';
import {
  isTableGroupIndex,
  isTableOrderIndex,
  Table,
  TableCreatorOp,
  TableDriver,
  TableEntityReplace,
  TableFindAllOp,
  TableFindByGroupOp,
  TableFindByIdOp,
  TableFindByIdSetOp,
  TableFindByOrderOp,
  TableIndexBuilder,
  TableIndexDriver,
  TableInsertAllOp,
  TableInsertOp,
  TableIsEmptyOp,
  TableMapperOfOp,
  TableMergeAllByIdOp,
  TableMergeAllOp,
  TableMergeByIdOp,
  TableMergeOp,
  TableRemoveAllOp,
  TableRemoveOp,
  TableReplaceAllOp,
  TableUpdateAllOp,
  TableUpdateOp,
} from './table.interfaces';

export const EMPTY_TABLE: Table<any> = { isEmpty: true, rows: {}, indexes: {} };

export function createTableDriver<T>(getId: TransformFn<T, ID>,
                                     ...indexDrivers: TableIndexDriver<T>[]): TableDriver<T> {
  const indexDriverByName = keyBy(indexDrivers, 'name');

  const bulkUpdate = (originalTable: Table<T>,
                      op: (rowDataBuilder: TableRowDataBuilder<T>, indexBuilder: TableIndexBuilder<T>[]) => void) => {
    const { rows: rowData, indexes } = originalTable;
    const rowDataBuilder = new TableRowDataBuilder(rowData);
    const indexBuilders = indexDrivers.map((indexDriver) => indexDriver.modify(indexes[indexDriver.name]));

    op(rowDataBuilder, indexBuilders);

    const newRowData = rowDataBuilder.build();
    const allIndexes = indexBuilders.map((indexBuilder) => indexBuilder.build());
    const updatedIndexes = allIndexes.filter((index, i) => index !== indexes[indexDrivers[i].name]);

    if (newRowData !== rowData) {
      return {
        isEmpty: rowDataBuilder.isEmpty(),
        rows: newRowData,
        indexes: updatedIndexes.length > 0 ?
          fromPairs(allIndexes.map((index, i) => [indexDrivers[i].name, index])) : indexes,
      };
    } else {
      return originalTable;
    }
  };

  const create: TableCreatorOp<T> = (iterator?: Iterator<T>) => {
    const table = {
      isEmpty: true,
      rows: {},
      indexes: fromPairs(indexDrivers.map((indexDriver) => [indexDriver.name, indexDriver.create()])),
    };

    if (iterator) {
      return bulkUpdate(table, (rowDataBuilder, indexBuilders) => {
        while (true) {
          const tuple = iterator.next();

          if (tuple.done) {
            break;
          }

          const data = tuple.value!;
          const id = getId(data);

          // build row
          rowDataBuilder.set(id, data);

          // update index for row
          indexBuilders.forEach((indexBuilder, i) => indexBuilder.insert(data));
        }
      });
    } else {
      return table;
    }
  };

  const insertAll: TableInsertAllOp<T> = (originalTable, newRows) =>
    bulkUpdate(originalTable, (rowDataBuilder, indexBuilders) => {
      const rowData = originalTable.rows;

      newRows.forEach((newRow) => {
        const id = getId(newRow);
        const originalRow = rowData[id];

        // if there is a row having the same id => raise an error
        if (originalRow) {
          throw new Error(`Table already has row for ID = '${id}'`);
        }

        rowDataBuilder.set(id, newRow);
        indexBuilders.forEach((indexBuilder) => indexBuilder.insert(newRow));
      });
    });

  const updateAll: TableUpdateAllOp<T> = (originalTable, updatedRows) =>
    bulkUpdate(originalTable, (rowDataBuilder, indexBuilders) => {
      const rowData = originalTable.rows;

      updatedRows.forEach((updatedRow) => {
        const id = getId(updatedRow);
        const originalRow = rowData[id];

        // if there is a row having the same id => raise an error
        if (isNil(originalRow)) {
          throw new Error(`Table does not have row for ID = '${id}'`);
        } else if (originalRow === updatedRow) {
          return;
        }

        rowDataBuilder.set(id, updatedRow);
        indexBuilders.forEach((indexBuilder) => indexBuilder.update(originalRow, updatedRow));
      });
    });

  const removeAll: TableRemoveAllOp<T> = (originalTable, ids) =>
    bulkUpdate(originalTable, (rowDataBuilder, indexBuilders) => {
      const rowData = originalTable.rows;

      ids.forEach((id) => {
        const originalRow = rowData[id];

        // if there is a row having the same id => raise an error
        if (isNil(originalRow)) {
          return;
        }

        rowDataBuilder.remove(id);
        indexBuilders.forEach((indexBuilder) => indexBuilder.remove(originalRow));
      });
    });

  const mergeAll: TableMergeAllOp<T> = (originalTable, dataSet, transform) =>
    bulkUpdate(originalTable, (rowDataBuilder, indexBuilders) => {
      const rowData = originalTable.rows;

      dataSet.forEach((dataItem) => {
        const id = getId(dataItem);
        const originalRow = rowData[id];

        if (originalRow) {
          const updatedRow = transform(originalRow, dataItem, id);
          if (updatedRow === originalRow) {
            // nothing to do
          } else if (updatedRow) {
            rowDataBuilder.set(id, updatedRow);
            indexBuilders.forEach((indexBuilder) => indexBuilder.update(originalRow, updatedRow));
          } else {
            rowDataBuilder.remove(id);
            indexBuilders.forEach((indexBuilder) => indexBuilder.remove(originalRow));
          }
        } else {
          const newRow = transform(originalRow, dataItem, id);

          if (newRow) {
            rowDataBuilder.set(id, newRow);
            indexBuilders.forEach((indexBuilder) => indexBuilder.insert(newRow));
          }
        }
      });
    });

  const mergeAllById: TableMergeAllByIdOp<T> = (originalTable, ids, transform) =>
    bulkUpdate(originalTable, (rowDataBuilder, indexBuilders) => {
      const rowData = originalTable.rows;

      ids.forEach((id) => {
        const originalRow = rowData[id];

        if (originalRow) {
          const updatedRow = transform(originalRow, id);
          if (updatedRow === originalRow) {
            // nothing to do
          } else if (updatedRow) {
            rowDataBuilder.set(id, updatedRow);
            indexBuilders.forEach((indexBuilder) => indexBuilder.update(originalRow, updatedRow));
          } else {
            rowDataBuilder.remove(id);
            indexBuilders.forEach((indexBuilder) => indexBuilder.remove(originalRow));
          }
        } else {
          const newRow = transform(originalRow, id);

          if (newRow) {
            rowDataBuilder.set(id, newRow);
            indexBuilders.forEach((indexBuilder) => indexBuilder.insert(newRow));
          }
        }
      });
    });

  const replaceAll: TableReplaceAllOp<T> = (originalTable: Table<T>,
                                            oldIds: ID[],
                                            dataSet: T[],
                                            replace: TableEntityReplace<T>) =>
    bulkUpdate(originalTable, (rowDataBuilder, indexBuilders) => {
      const rowData = originalTable.rows;
      const processedIndex: Hash<boolean> = {};
      const dataSetIndex = keyBy(dataSet, getId);

      oldIds.forEach((oldId) => {
        const originalRow = rowData[oldId];
        const dataItem = dataSetIndex[oldId];

        if (originalRow) {
          const updatedRow = replace(originalRow, dataItem, oldId);
          if (updatedRow === originalRow) {
            // nothing to do
          } else if (updatedRow) {
            rowDataBuilder.set(oldId, updatedRow);
            indexBuilders.forEach((indexBuilder) => indexBuilder.update(originalRow, updatedRow));
          } else {
            rowDataBuilder.remove(oldId);
            indexBuilders.forEach((indexBuilder) => indexBuilder.remove(originalRow));
          }
        } else {
          const newRow = replace(originalRow, dataItem, oldId);

          if (newRow) {
            rowDataBuilder.set(oldId, newRow);
            indexBuilders.forEach((indexBuilder) => indexBuilder.insert(newRow));
          }
        }

        processedIndex[oldId] = true;
      });

      dataSet.forEach((dataItem) => {
        const id = getId(dataItem);

        if (processedIndex[id]) {
          return;
        }

        const originalRow = rowData[id];

        if (originalRow) {
          const updatedRow = replace(originalRow, dataItem, id);
          if (updatedRow === originalRow) {
            // nothing to do
          } else if (updatedRow) {
            rowDataBuilder.set(id, updatedRow);
            indexBuilders.forEach((indexBuilder) => indexBuilder.update(originalRow, updatedRow));
          } else {
            rowDataBuilder.remove(id);
            indexBuilders.forEach((indexBuilder) => indexBuilder.remove(originalRow));
          }
        } else {
          const newRow = replace(originalRow, dataItem, id);

          if (newRow) {
            rowDataBuilder.set(id, newRow);
            indexBuilders.forEach((indexBuilder) => indexBuilder.insert(newRow));
          }
        }
      });
    });

  const byId: TableFindByIdOp<T> = (table: Table<T>, id: ID) => table.rows[id];
  const byIdSet: TableFindByIdSetOp<T> = (table: Table<T>, ids: ID[]) => ids.map((id) =>
    table.rows[id]).filter(Boolean);

  const byGroup: TableFindByGroupOp<T> = (table: Table<T>, group: string, value: any) => {
    const indexDriver = indexDriverByName[group];

    if (isNil(indexDriver)) {
      throw new Error(`Index for name '${group}' does not exist`);
    } else if (isTableGroupIndex(indexDriver)) {
      return indexDriver.getGroupedData(table.indexes[group], value);
    } else {
      throw new Error(`Table index driver for index '${group}' does not support TableGroupIndex interface`);
    }
  };

  const byOrder: TableFindByOrderOp<T> = (table: Table<T>, order: string) => {
    const indexDriver = indexDriverByName[order];

    if (isNil(indexDriver)) {
      throw new Error(`Index for name '${order}' does not exist`);
    } else if (isTableOrderIndex(indexDriver)) {
      return indexDriver.getOrderedData(table.indexes[order]);
    } else {
      throw new Error(`Table index driver for index '${order}' does not support TableGroupIndex interface`);
    }
  };

  const insert: TableInsertOp<T> = (table, row) => insertAll(table, [row]);
  const update: TableUpdateOp<T> = (table, row) => updateAll(table, [row]);
  const remove: TableRemoveOp<T> = (table, id) => removeAll(table, [id]);
  const mergeById: TableMergeByIdOp<T> = (table, id, transform) => mergeAllById(table, [id], transform);
  const merge: TableMergeOp<T> = (table, row, transform) => mergeAll(table, [row], transform);
  const all: TableFindAllOp<T> = ({ rows }) => Object.keys(rows).map((id) => rows[id]);
  const mapperOf: TableMapperOfOp<T> = (entityMapper) => createTableMapper(create, all, entityMapper);
  const isEmpty: TableIsEmptyOp<T> = (table) => table.isEmpty;

  return {
    getId,
    create,
    insert,
    update,
    remove,
    insertAll,
    updateAll,
    removeAll,
    mergeById,
    mergeAllById,
    merge,
    mergeAll,
    replaceAll,
    all,
    byId,
    byIdSet,
    byGroup,
    byOrder,
    mapperOf,
    isEmpty,
  };
}

class TableRowDataBuilder<T> {
  private copied = false;
  private _isEmpty?: boolean;

  constructor(private rows: Hash<T>) {

  }

  set(id: ID, data: T): void {
    this.copy();
    this.rows[id] = data;
    this._isEmpty = false;
  }

  remove(id: ID): void {
    this.copy();
    delete this.rows[id];
    this._isEmpty = void 0;
  }

  build(): Hash<T> {
    return this.rows;
  }

  isEmpty(): boolean {
    return coalesce(this._isEmpty, this.calculateIsEmpty());
  }

  private copy(): void {
    if (!this.copied) {
      this.rows = { ...this.rows };
      this.copied = true;
    }
  }

  private calculateIsEmpty(): boolean {
    const { rows } = this;
    for (const key in rows) {
      if (Object.prototype.hasOwnProperty.call(rows, key)) {
        return false;
      }
    }
    return true;
  }
}
